/**
 * GlobalLoadingContext
 * Context for managing global loading state across the application.
 *
 * Two ways to drive the overlay:
 *
 * 1. Manual (explicit actions): startLoading() / stopLoading() — used for
 *    discrete blocking operations like login or file upload.
 *
 * 2. Data-aware (page loads): registerLoading(key) / unregisterLoading(key),
 *    normally via the usePageLoading() hook. The overlay stays up until the
 *    page's own loading flag flips false — no fixed timer, so it never
 *    dismisses while a table or chart is still fetching.
 */

import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

export interface LoadingOptions {
  message?: string;
  submessage?: string;
  showProgress?: boolean;
  progress?: number;
  showOverlay?: boolean;
  type?: 'default' | 'navigation' | 'api' | 'auth';
}

const DEFAULT_OPTIONS: LoadingOptions = {
  message: 'Loading...',
  submessage: 'Please wait',
  showProgress: false,
  progress: 0,
  showOverlay: true,
  type: 'default',
};

interface GlobalLoadingContextType {
  isLoading: boolean;
  options: LoadingOptions;
  startLoading: (options?: LoadingOptions) => void;
  stopLoading: () => void;
  updateProgress: (progress: number) => void;
  updateMessage: (message: string, submessage?: string) => void;
  /** Register a data-loading task (overlay stays up while any task is active). */
  registerLoading: (key: string, options?: LoadingOptions) => void;
  /** Clear a previously registered data-loading task. */
  unregisterLoading: (key: string) => void;
}

const GlobalLoadingContext = createContext<GlobalLoadingContextType | undefined>(undefined);

interface GlobalLoadingProviderProps {
  children: ReactNode;
}

export function GlobalLoadingProvider({ children }: GlobalLoadingProviderProps) {
  // Manual, explicit-action loading (login, upload, …)
  const [manualLoading, setManualLoading] = useState(false);
  const [manualOptions, setManualOptions] = useState<LoadingOptions>(DEFAULT_OPTIONS);

  // Data-aware page-load tasks, keyed so multiple can overlap safely.
  const [tasks, setTasks] = useState<Record<string, LoadingOptions>>({});

  const startLoading = useCallback((newOptions?: LoadingOptions) => {
    setManualOptions({ ...DEFAULT_OPTIONS, ...newOptions });
    setManualLoading(true);
  }, []);

  const stopLoading = useCallback(() => {
    setManualLoading(false);
    // Reset to defaults after a short delay
    setTimeout(() => {
      setManualOptions(DEFAULT_OPTIONS);
    }, 300);
  }, []);

  const updateProgress = useCallback((progress: number) => {
    setManualOptions((prev) => ({
      ...prev,
      progress: Math.min(100, Math.max(0, progress)),
      showProgress: true,
    }));
  }, []);

  const updateMessage = useCallback((message: string, submessage?: string) => {
    setManualOptions((prev) => ({
      ...prev,
      message,
      ...(submessage !== undefined && { submessage }),
    }));
  }, []);

  const registerLoading = useCallback((key: string, options?: LoadingOptions) => {
    setTasks((prev) => ({ ...prev, [key]: { ...DEFAULT_OPTIONS, ...options } }));
  }, []);

  const unregisterLoading = useCallback((key: string) => {
    setTasks((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // Manual loading wins (it carries progress/custom copy). Otherwise, if any
  // page task is active, surface the most recently registered one's options.
  const taskKeys = Object.keys(tasks);
  const isLoading = manualLoading || taskKeys.length > 0;
  const options = useMemo<LoadingOptions>(() => {
    if (manualLoading) return manualOptions;
    const lastKey = taskKeys[taskKeys.length - 1];
    return lastKey ? tasks[lastKey] : DEFAULT_OPTIONS;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualLoading, manualOptions, tasks]);

  const value: GlobalLoadingContextType = {
    isLoading,
    options,
    startLoading,
    stopLoading,
    updateProgress,
    updateMessage,
    registerLoading,
    unregisterLoading,
  };

  return (
    <GlobalLoadingContext.Provider value={value}>
      {children}
    </GlobalLoadingContext.Provider>
  );
}

export function useGlobalLoadingContext() {
  const context = useContext(GlobalLoadingContext);
  if (context === undefined) {
    throw new Error('useGlobalLoadingContext must be used within a GlobalLoadingProvider');
  }
  return context;
}
